import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { ArrowLeft, Sparkles, Check, X, Loader2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Iter-172 — Admin AI Creator approval queue + avatar approval queue.
 */
const AdminAICreators = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('creators'); // 'creators' | 'avatars'
  const [creators, setCreators] = useState([]);
  const [avatars, setAvatars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [reject, setReject] = useState({ open: false, kind: null, id: null, reason: '' });

  const load = async () => {
    setLoading(true);
    try {
      const [c, a] = await Promise.all([
        api.get(`/admin/ai-creators${filter === 'all' ? '' : `?status=${filter}`}`),
        api.get('/admin/ai-avatars/queue'),
      ]);
      setCreators(c.data || []);
      setAvatars(a.data || []);
    } catch (e) {
      toast.error('Failed to load queue');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filter]);

  const approveCreator = async (uid) => {
    try { await api.post(`/admin/ai-creators/${uid}/approve`); toast.success('Creator approved'); load(); }
    catch { toast.error('Approve failed'); }
  };
  const approveAvatar = async (uid) => {
    try { await api.post(`/admin/ai-avatars/${uid}/approve`); toast.success('Avatar approved'); load(); }
    catch { toast.error('Approve failed'); }
  };
  const submitReject = async () => {
    try {
      const path = reject.kind === 'creator'
        ? `/admin/ai-creators/${reject.id}/reject`
        : `/admin/ai-avatars/${reject.id}/reject`;
      await api.post(path, { reason: reject.reason });
      toast.success('Rejected');
      setReject({ open: false, kind: null, id: null, reason: '' });
      load();
    } catch { toast.error('Reject failed'); }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-ai-creators-page">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/home')} data-testid="back-button">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <h1 className="text-xl font-bold flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-fuchsia-500" /> AI Creator Approvals
            </h1>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-4 pb-3 flex items-center gap-2">
          <Button size="sm" variant={tab === 'creators' ? 'default' : 'outline'} onClick={() => setTab('creators')} className={tab === 'creators' ? 'bg-fuchsia-500 hover:bg-fuchsia-600 text-white' : ''} data-testid="tab-creators">
            Creators {creators.filter(c => c.status === 'pending').length > 0 && <Badge className="ml-2 bg-rose-500 text-white">{creators.filter(c => c.status === 'pending').length}</Badge>}
          </Button>
          <Button size="sm" variant={tab === 'avatars' ? 'default' : 'outline'} onClick={() => setTab('avatars')} className={tab === 'avatars' ? 'bg-fuchsia-500 hover:bg-fuchsia-600 text-white' : ''} data-testid="tab-avatars">
            Avatars {avatars.length > 0 && <Badge className="ml-2 bg-rose-500 text-white">{avatars.length}</Badge>}
          </Button>
          {tab === 'creators' && (
            <select className="ml-auto h-8 border border-gray-200 rounded text-sm px-2" value={filter} onChange={e => setFilter(e.target.value)} data-testid="filter-status">
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="all">All</option>
            </select>
          )}
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-fuchsia-500" /></div>
        ) : tab === 'creators' ? (
          creators.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-gray-500">No creators in this filter.</CardContent></Card>
          ) : (
            <div className="space-y-3" data-testid="creators-list">
              {creators.map(c => (
                <Card key={c.user_id} data-testid={`creator-row-${c.user_id}`}>
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-violet-100 to-fuchsia-100 flex items-center justify-center shrink-0">
                      <Sparkles className="w-5 h-5 text-fuchsia-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold truncate">{c.creator_name}</h3>
                        <Badge variant="outline" className={
                          c.status === 'approved' ? 'border-emerald-300 text-emerald-700' :
                          c.status === 'rejected' ? 'border-rose-300 text-rose-700' :
                          'border-amber-300 text-amber-700'
                        }>{c.status}</Badge>
                        {c.studio_name && <span className="text-xs text-gray-500">· {c.studio_name}</span>}
                      </div>
                      <p className="text-xs text-gray-500 truncate mt-0.5">{c.email}</p>
                      {c.bio && <p className="text-xs text-gray-600 truncate mt-1">{c.bio}</p>}
                      <p className="text-[11px] text-gray-400 mt-1">
                        {c.avatar_count} avatars · {c.pending_avatars} pending
                        {c.portfolio_url && <> · <a href={c.portfolio_url} target="_blank" rel="noreferrer" className="text-cyan-600 hover:underline inline-flex items-center gap-0.5">Portfolio <ExternalLink className="w-2.5 h-2.5" /></a></>}
                      </p>
                    </div>
                    {c.status === 'pending' && (
                      <div className="flex items-center gap-2 shrink-0">
                        <Button size="sm" onClick={() => approveCreator(c.user_id)} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid={`approve-creator-${c.user_id}`}>
                          <Check className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setReject({ open: true, kind: 'creator', id: c.user_id, reason: '' })} className="border-rose-300 text-rose-700" data-testid={`reject-creator-${c.user_id}`}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )
        ) : (
          avatars.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-gray-500">No pending avatars.</CardContent></Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="avatars-list">
              {avatars.map(a => (
                <Card key={a.avatar_user_id} className="overflow-hidden" data-testid={`avatar-row-${a.avatar_user_id}`}>
                  <div className="aspect-square bg-gradient-to-br from-violet-100 to-fuchsia-100">
                    {a.avatar_url
                      ? <img src={a.avatar_url} alt="" className="w-full h-full object-cover" />
                      : <div className="w-full h-full flex items-center justify-center"><Sparkles className="w-10 h-10 text-fuchsia-400" /></div>}
                  </div>
                  <CardContent className="p-3">
                    <h3 className="font-semibold text-sm truncate">{a.first_name} {a.last_name}</h3>
                    <p className="text-xs text-gray-500 truncate">by {a.creator_name || 'Admin'} · {a.style || 'unspecified'}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <Button size="sm" onClick={() => approveAvatar(a.avatar_user_id)} className="bg-emerald-500 hover:bg-emerald-600 text-white flex-1" data-testid={`approve-avatar-${a.avatar_user_id}`}>
                        <Check className="w-3.5 h-3.5 mr-1" /> Approve
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setReject({ open: true, kind: 'avatar', id: a.avatar_user_id, reason: '' })} className="border-rose-300 text-rose-700" data-testid={`reject-avatar-${a.avatar_user_id}`}>
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )
        )}
      </div>

      <Dialog open={reject.open} onOpenChange={(v) => !v && setReject({ open: false, kind: null, id: null, reason: '' })}>
        <DialogContent data-testid="reject-dialog">
          <DialogHeader><DialogTitle>Reject {reject.kind}</DialogTitle></DialogHeader>
          <Textarea
            rows={3}
            placeholder="Reason (visible to creator)"
            value={reject.reason}
            onChange={e => setReject(r => ({ ...r, reason: e.target.value }))}
            data-testid="reject-reason"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setReject({ open: false, kind: null, id: null, reason: '' })}>Cancel</Button>
            <Button onClick={submitReject} className="bg-rose-500 hover:bg-rose-600 text-white" data-testid="reject-confirm">Reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminAICreators;
